import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useGate, useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import * as yup from 'yup';

import {LanguageSelector} from 'components/common/LanguageSelector';
import {Button} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {TitleLogoComponent} from 'components/ui-kit/TitleLogo';
import {LABELS} from 'core/constants/labels';

import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {doLogout} from 'models/auth';
import {Gate, setPasswordFx, state$} from 'models/password';

import {getPasswordSchema} from './helpers/passwordSchema';
import {PasswordValidationRules} from './helpers/passwordValidationRules';
import {
    ButtonContainer,
    Field,
    Fields,
    Form,
    FormDescription,
    LanguageWrapper,
    MainContainer,
    PasswordContent,
    PasswordTitle,
    ServiceTitle,
    MessageWrapper,
} from './styled';

type SetPasswordForm = {password: string; passwordConfirm: string};

export function SetPasswordPage() {
    const {t, i18n} = useTranslation();
    const {search} = useLocation();
    useGate(Gate);

    const token = useMemo(() => new URLSearchParams(search).get('token'), [search]);
    const state = useStore(state$);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) =>
            t(`pages.password.validations.${key}`, replace),
        [i18n.language],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                password: getPasswordSchema(translateValidates),
                passwordConfirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .oneOf([yup.ref('password')], translateValidates('confirm_password_mismatch')),
            }),
        [translateValidates],
    );

    const {control, handleSubmit, formState, watch, errors} = useForm({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            password: '',
            passwordConfirm: '',
        },
    });

    const watchPassword = watch('password', '');

    const onLogin = () => history.push(ROUTE.LOGIN);

    useEffect(() => {
        doLogout(false);
    }, []);

    const onSubmit = async (data: SetPasswordForm) => {
        if (!token) return;
        try {
            await setPasswordFx({
                tokenType: 'SET_PASSWORD',
                password: data.password,
                confirmPassword: data.passwordConfirm,
                token,
            });
            history.push(ROUTE.LOGIN);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            <div>
                <ServiceTitle size={4}>
                    <TitleLogoComponent />
                    {LABELS.name}
                </ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <PasswordContent>
                    {!state.success && !state.expired && (
                        <>
                            <PasswordTitle size={3}>{t('pages.password.set.title')}</PasswordTitle>
                            <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                                <FormDescription>
                                    {t('pages.password.set.description')}
                                </FormDescription>
                                <Fields>
                                    <Field isError={!!errors.password}>
                                        <span>{t('pages.password.set.fields.password.label')}</span>
                                        <Controller
                                            name="password"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    type="password"
                                                    placeholder={t(
                                                        'pages.password.set.fields.password.placeholder',
                                                    )}
                                                    autoComplete="new-password"
                                                    disabled={state.pending}
                                                    hasError={!!errors.password}
                                                    isValid={
                                                        formState.dirtyFields.password &&
                                                        !errors.password
                                                    }
                                                />
                                            )}
                                        />
                                        <PasswordValidationRules watchValue={watchPassword} />
                                    </Field>

                                    <Field
                                        isError={!!errors.passwordConfirm}
                                        text={errors.passwordConfirm?.message}
                                    >
                                        <span>
                                            {t(
                                                'pages.password.set.fields.passwordConfirmation.label',
                                            )}
                                        </span>
                                        <Controller
                                            name="passwordConfirm"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    type="password"
                                                    placeholder={t(
                                                        'pages.password.set.fields.passwordConfirmation.placeholder',
                                                    )}
                                                    autoComplete="new-password"
                                                    disabled={state.pending}
                                                    hasError={!!errors.passwordConfirm}
                                                    isValid={
                                                        formState.dirtyFields.passwordConfirm &&
                                                        !errors.passwordConfirm
                                                    }
                                                />
                                            )}
                                        />
                                    </Field>
                                </Fields>

                                <ButtonContainer>
                                    <Button
                                        type="submit"
                                        isLoading={state.pending}
                                        disabled={!formState.isValid}
                                        data-id="btn-submit"
                                    >
                                        {t('pages.password.set.submitButton')}
                                    </Button>
                                </ButtonContainer>
                            </Form>
                        </>
                    )}
                    {state.success && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('pages.password.set.success.title')}
                            </PasswordTitle>
                            <Button onClick={onLogin}>
                                {t('pages.password.set.success.submit')}
                            </Button>
                        </MessageWrapper>
                    )}
                    {state.expired && (
                        <MessageWrapper>
                            <PasswordTitle size={3}>
                                {t('pages.password.set.expired.title')}
                            </PasswordTitle>
                        </MessageWrapper>
                    )}
                </PasswordContent>
            </MainContainer>
        </>
    );
}
